import React, { useState, useCallback } from 'react';
import { X, ChevronRight, ChevronDown, Folder, Database, Table as TableIcon, FileJson, Search, CheckCircle2, Check, List, Layers, File, Loader2 } from 'lucide-react';

interface SchemaNode {
    name: string;
    type: string;
    dataType?: string | null;
    children?: SchemaNode[];
    path?: string;
    expandable?: boolean;
    icon?: string;
}

interface Props {
    onClose: () => void;
    schemaData: SchemaNode[];
    onSelect: (paths: string[]) => void;
    onExpand?: (nodePath: string) => Promise<SchemaNode[]>;
    /** Optional: build a metadata JSON template from the current selection */
    onBuildMetadata?: (metadataJson: string) => void;
    /** Optional: connector/source type hint, e.g. 'postgresql', 's3' */
    sourceType?: string;
}

const getNodeByPath = (nodes: SchemaNode[], targetPath: string, basePath: string = ''): SchemaNode | null => {
    for (const node of nodes) {
        const currentPath = basePath ? `${basePath}.${node.name}` : node.name;
        if (currentPath === targetPath) {
            return node;
        }
        if (node.children?.length) {
            const found = getNodeByPath(node.children, targetPath, currentPath);
            if (found) return found;
        }
    }
    return null;
};

const humanizeName = (raw: string): string => {
    if (!raw) return raw;
    // Split camelCase / PascalCase / snake_case
    const spaced = raw
        .replace(/[_\-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const buildRelationalMetadata = (treeData: SchemaNode[], selectedPaths: string[], sourceTypeHint?: string): string => {
    const normalized = (sourceTypeHint || '').toLowerCase() || 'postgresql';
    const category = 'relational_database';

    const columnEntries: Array<{
        databaseId: string;
        schemaId: string;
        tableId: string;
        columnName: string;
        dataType: string | null | undefined;
    }> = [];

    const uniquePaths = Array.from(new Set(selectedPaths));

    for (const path of uniquePaths) {
        const node = getNodeByPath(treeData, path);
        if (!node) continue;
        const type = (node.type || '').toLowerCase();
        if (type !== 'column' && type !== 'field') continue;

        const parts = path.split('.');
        if (parts.length < 4) continue; // expect db.schema.table.column

        const databaseId = parts[0];
        const schemaId = parts[1];
        const tableId = parts[2];
        const columnName = parts[parts.length - 1];

        columnEntries.push({
            databaseId,
            schemaId,
            tableId,
            columnName,
            dataType: node.dataType
        });
    }

    if (columnEntries.length === 0) {
        // Fallback: empty but well-formed template
        return JSON.stringify({
            metadata: {
                version: '1.0',
                generated_at: new Date().toISOString(),
                source: {
                    id: '<database_id>',
                    display_name: '<Database>',
                    source_type: normalized,
                    category,
                    connection_ref: '<connection_ref>'
                },
                catalog: []
            }
        }, null, 2);
    }

    const databaseId = columnEntries[0].databaseId;

    const catalogMap = new Map<string, any>();

    for (const entry of columnEntries) {
        const { schemaId, tableId, columnName, dataType } = entry;

        if (!catalogMap.has(schemaId)) {
            catalogMap.set(schemaId, {
                id: schemaId,
                display_name: humanizeName(schemaId),
                parent_type: 'schema',
                parent_id: databaseId,
                source_type: normalized,
                category,
                entities: [] as any[]
            });
        }
        const catalogEntry = catalogMap.get(schemaId);

        const entityId = `${schemaId}.${tableId}`;
        let entity = catalogEntry.entities.find((e: any) => e.id === entityId);
        if (!entity) {
            entity = {
                id: entityId,
                display_name: humanizeName(tableId),
                entity_type: 'table',
                parent_id: schemaId,
                parent_type: 'schema',
                source_type: normalized,
                category,
                query_hint: `SELECT * FROM ${schemaId}."${tableId}"`,
                fields: [] as any[]
            };
            catalogEntry.entities.push(entity);
        }

        entity.fields.push({
            name: columnName,
            display_name: humanizeName(columnName),
            data_type: dataType || 'string',
            parent_entity: entityId,
            parent_type: 'table',
            nullable: true,
            is_primary_key: false,
            is_foreign_key: false,
            source_type: normalized,
            category
        });
    }

    const metadata = {
        metadata: {
            version: '1.0',
            generated_at: new Date().toISOString(),
            source: {
                id: databaseId,
                display_name: humanizeName(databaseId),
                source_type: normalized,
                category,
                connection_ref: databaseId
            },
            catalog: Array.from(catalogMap.values())
        }
    };

    return JSON.stringify(metadata, null, 2);
};

const buildObjectStorageMetadata = (treeData: SchemaNode[], selectedPaths: string[], sourceTypeHint?: string): string => {
    const normalized = (sourceTypeHint || '').toLowerCase() || 's3';
    const category = 'object_storage';

    const fieldEntries: Array<{
        bucketId: string;
        prefixId: string;
        fileName: string;
        columnName: string;
        dataType: string | null | undefined;
    }> = [];

    const uniquePaths = Array.from(new Set(selectedPaths));

    for (const path of uniquePaths) {
        const node = getNodeByPath(treeData, path);
        if (!node) continue;
        const type = (node.type || '').toLowerCase();
        if (type !== 'column' && type !== 'field') continue;

        const parts = path.split('.');
        if (parts.length < 4) continue; // expect bucket.prefix....file.column

        const bucketId = parts[0];
        const columnName = parts[parts.length - 1];
        const fileName = parts[parts.length - 2];
        const prefixParts = parts.slice(1, parts.length - 2);
        const prefixId = prefixParts.join('/') || fileName;

        fieldEntries.push({
            bucketId,
            prefixId,
            fileName,
            columnName,
            dataType: node.dataType
        });
    }

    if (fieldEntries.length === 0) {
        return JSON.stringify({
            source: {
                id: '<bucket_id>',
                display_name: '<Bucket>',
                source_type: normalized,
                category,
                connection_ref: '<connection_ref>'
            },
            catalog: []
        }, null, 2);
    }

    const bucketId = fieldEntries[0].bucketId;
    const catalogMap = new Map<string, any>();

    for (const entry of fieldEntries) {
        const { prefixId, fileName, columnName, dataType } = entry;

        if (!catalogMap.has(prefixId)) {
            const displayName = prefixId.split('/').slice(-1)[0] || prefixId;
            catalogMap.set(prefixId, {
                id: prefixId,
                display_name: humanizeName(displayName),
                parent_type: 'bucket',
                parent_id: bucketId,
                source_type: normalized,
                category,
                entities: [] as any[]
            });
        }
        const catalogEntry = catalogMap.get(prefixId);

        const entityId = `${prefixId}/${fileName}`;
        let entity = catalogEntry.entities.find((e: any) => e.id === entityId);
        if (!entity) {
            entity = {
                id: entityId,
                display_name: fileName,
                entity_type: 'file',
                parent_id: prefixId,
                parent_type: 'prefix',
                source_type: normalized,
                category,
                query_hint: `${normalized}://${bucketId}/${entityId}`,
                fields: [] as any[]
            };
            catalogEntry.entities.push(entity);
        }

        entity.fields.push({
            name: columnName,
            data_type: dataType || 'string',
            parent_entity: entityId,
            parent_type: 'file',
            source_type: normalized,
            category
        });
    }

    const metadata = {
        source: {
            id: bucketId,
            display_name: humanizeName(bucketId),
            source_type: normalized,
            category,
            connection_ref: bucketId
        },
        catalog: Array.from(catalogMap.values())
    };

    return JSON.stringify(metadata, null, 2);
};

/** OneDrive / SharePoint / Google Drive / Dropbox: drive → folders → files (optional: file → fields) */
const buildFileStorageMetadata = (treeData: SchemaNode[], selectedPaths: string[], sourceTypeHint?: string): string => {
    const raw = (sourceTypeHint || '').toLowerCase();
    const normalized = raw.includes('one') ? 'onedrive' : raw.includes('sharepoint') ? 'sharepoint' : raw.includes('drive') || raw.includes('gdrive') ? 'google_drive' : raw.includes('dropbox') ? 'dropbox' : raw || 'file_storage';
    const category = 'file_storage';

    const uniquePaths = Array.from(new Set(selectedPaths));
    const rootId = uniquePaths[0]?.split('.')[0] || '<drive_id>';

    type CatalogEntry = { id: string; display_name: string; parent_type: string; parent_id: string; source_type: string; category: string; entities: any[] };
    const catalogMap = new Map<string, CatalogEntry>();

    const ensureCatalogEntry = (folderPath: string, parentId: string, parentType: string) => {
        if (!folderPath) return;
        if (!catalogMap.has(folderPath)) {
            const displayName = folderPath.split('.').pop() || folderPath;
            catalogMap.set(folderPath, {
                id: folderPath,
                display_name: humanizeName(displayName),
                parent_type: parentType,
                parent_id: parentId,
                source_type: normalized,
                category,
                entities: [] as any[]
            });
        }
    };

    for (const path of uniquePaths) {
        const node = getNodeByPath(treeData, path);
        if (!node) continue;
        const type = (node.type || '').toLowerCase();
        const parts = path.split('.');

        if (type === 'database' || (type === 'folder' && parts.length === 1)) {
            ensureCatalogEntry(parts[0], rootId, 'drive');
            continue;
        }

        if (type === 'folder') {
            const folderPath = parts.join('.');
            const parentPath = parts.length > 1 ? parts.slice(0, -1).join('.') : rootId;
            ensureCatalogEntry(folderPath, parentPath === rootId ? rootId : parentPath, parts.length === 2 ? 'drive' : 'folder');
            continue;
        }

        if (type === 'file' || type === 'table' || type === 'collection') {
            const entityId = path;
            const parentPath = parts.length > 1 ? parts.slice(0, -1).join('.') : rootId;
            ensureCatalogEntry(parentPath, parentPath === rootId ? rootId : parentPath, parts.length === 2 ? 'drive' : 'folder');
            const catalogEntry = catalogMap.get(parentPath);
            if (!catalogEntry) continue;
            let entity = catalogEntry.entities.find((e: any) => e.id === entityId);
            if (!entity) {
                entity = {
                    id: entityId,
                    display_name: humanizeName(parts[parts.length - 1]),
                    entity_type: type === 'table' || type === 'collection' ? 'table' : 'file',
                    parent_id: parentPath,
                    parent_type: parentPath === rootId ? 'drive' : 'folder',
                    source_type: normalized,
                    category,
                    query_hint: path,
                    fields: [] as any[]
                };
                catalogEntry.entities.push(entity);
            }
            continue;
        }

        if (type === 'column' || type === 'field') {
            if (parts.length < 3) continue;
            const filePath = parts.slice(0, -1).join('.');
            const columnName = parts[parts.length - 1];
            const parentPath = parts.length > 2 ? parts.slice(0, -2).join('.') : rootId;
            ensureCatalogEntry(parentPath, parentPath === rootId ? rootId : parentPath, 'folder');
            const catalogEntry = catalogMap.get(parentPath);
            if (!catalogEntry) continue;
            let entity = catalogEntry.entities.find((e: any) => e.id === filePath);
            if (!entity) {
                entity = {
                    id: filePath,
                    display_name: humanizeName(parts[parts.length - 2]),
                    entity_type: 'file',
                    parent_id: parentPath,
                    parent_type: 'folder',
                    source_type: normalized,
                    category,
                    query_hint: filePath,
                    fields: [] as any[]
                };
                catalogEntry.entities.push(entity);
            }
            entity.fields.push({
                name: columnName,
                display_name: humanizeName(columnName),
                data_type: node.dataType || 'string',
                parent_entity: filePath,
                parent_type: 'file',
                source_type: normalized,
                category
            });
        }
    }

    const catalog = Array.from(catalogMap.values()).filter(c => c.id !== rootId || c.entities.length > 0);
    const metadata = {
        source: {
            id: rootId,
            display_name: humanizeName(rootId),
            source_type: normalized,
            category,
            connection_ref: rootId
        },
        catalog
    };
    return JSON.stringify(metadata, null, 2);
};

const TreeNode: React.FC<{
    node: SchemaNode;
    level: number;
    onToggle: (path: string) => void;
    isSelected: boolean;
    path: string;
    selectedPaths: string[];
    onExpand?: (nodePath: string) => Promise<SchemaNode[]>;
    onChildrenLoaded: (treePath: string, children: SchemaNode[]) => void;
}> = ({ node, level, onToggle, isSelected, path, selectedPaths, onExpand, onChildrenLoaded }) => {
    const [isOpen, setIsOpen] = useState(level === 0);
    const [isLoading, setIsLoading] = useState(false);

    const hasChildren = node.children && node.children.length > 0;
    const isLazyExpandable = node.expandable && !node.children;
    const canExpand = hasChildren || isLazyExpandable;
    const currentPath = path ? `${path}.${node.name}` : node.name;

    const getIcon = () => {
        switch (node.type.toLowerCase()) {
            case 'database': return <Database className="w-4 h-4 text-indigo-400" />;
            case 'folder': return <Folder className="w-4 h-4 text-amber-400" />;
            case 'table': return <TableIcon className="w-4 h-4 text-blue-400" />;
            case 'collection': return <FileJson className="w-4 h-4 text-green-400" />;
            case 'column': return <List className="w-4 h-4 text-purple-400" />;
            case 'field': return <Layers className="w-4 h-4 text-cyan-400" />;
            case 'file': return <File className="w-4 h-4 text-gray-400" />;
            default: return <TableIcon className="w-4 h-4 text-gray-400" />;
        }
    };

    const isParentOfSelected = (n: SchemaNode, p: string): boolean => {
        const fullPath = p ? `${p}.${n.name}` : n.name;
        return n.children?.some(child => {
            const childPath = `${fullPath}.${child.name}`;
            return selectedPaths.includes(childPath) || isParentOfSelected(child, fullPath);
        }) || false;
    };

    const parentSelected = !isSelected && isParentOfSelected(node, path);

    const handleExpand = async () => {
        if (isLoading) return;

        if (isLazyExpandable && onExpand && node.path) {
            setIsLoading(true);
            try {
                const children = await onExpand(node.path);
                onChildrenLoaded(currentPath, children);
                setIsOpen(true);
            } catch (err) {
                console.error('Failed to expand node:', err);
            } finally {
                setIsLoading(false);
            }
        } else if (canExpand) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className="select-none">
            <div
                className={`flex items-center gap-2 py-1.5 px-2 rounded-lg cursor-pointer hover:bg-neutral-800 transition-colors group ${level === 0 ? 'mt-1' : ''}`}
                style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
                onClick={() => {
                    if (canExpand) handleExpand();
                    else onToggle(currentPath);
                }}
            >
                <div className="w-4 h-4 flex items-center justify-center" onClick={(e) => {
                    if (canExpand) {
                        e.stopPropagation();
                        handleExpand();
                    }
                }}>
                    {isLoading ? (
                        <Loader2 className="w-3 h-3 text-indigo-400 animate-spin" />
                    ) : canExpand ? (
                        isOpen ? <ChevronDown className="w-3 h-3 text-gray-500" /> : <ChevronRight className="w-3 h-3 text-gray-500" />
                    ) : null}
                </div>

                <div
                    className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${isSelected
                        ? 'bg-indigo-600 border-indigo-600'
                        : parentSelected ? 'border-indigo-500/50 bg-indigo-500/10' : 'border-neutral-700 bg-neutral-800'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle(currentPath);
                    }}
                >
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                    {parentSelected && <div className="w-1.5 h-1.5 bg-indigo-400 rounded-sm" />}
                </div>

                {getIcon()}
                <span className={`text-sm transition-colors flex-1 min-w-0 truncate ${isSelected ? 'text-white font-medium' : parentSelected ? 'text-indigo-200' : 'text-gray-300'} group-hover:text-white`}>
                    {node.name}
                </span>

                {node.dataType && (
                    <span className="text-[10px] font-mono text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-0.5 shrink-0">
                        {node.dataType}
                    </span>
                )}

                {node.type && (
                    <span className="text-[10px] text-gray-600 ml-auto opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-tighter shrink-0">
                        {node.type}
                    </span>
                )}
            </div>

            {hasChildren && isOpen && (
                <div className="animate-in slide-in-from-top-1 duration-200">
                    {node.children!.map((child, idx) => (
                        <TreeNode
                            key={child.path || idx}
                            node={child}
                            level={level + 1}
                            onToggle={onToggle}
                            isSelected={selectedPaths.includes(`${currentPath}.${child.name}`)}
                            path={currentPath}
                            selectedPaths={selectedPaths}
                            onExpand={onExpand}
                            onChildrenLoaded={onChildrenLoaded}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const collectPathsUnder = (nodes: SchemaNode[], basePath: string): string[] => {
    const result: string[] = [];
    for (const node of nodes) {
        const currentPath = basePath ? `${basePath}.${node.name}` : node.name;
        result.push(currentPath);
        if (node.children?.length) {
            result.push(...collectPathsUnder(node.children, currentPath));
        }
    }
    return result;
};

const findPathsForNode = (nodes: SchemaNode[], targetPath: string, basePath: string = ''): string[] | null => {
    for (const node of nodes) {
        const currentPath = basePath ? `${basePath}.${node.name}` : node.name;
        if (currentPath === targetPath) {
            return [currentPath, ...collectPathsUnder(node.children ?? [], currentPath)];
        }
        if (node.children?.length) {
            const found = findPathsForNode(node.children, targetPath, currentPath);
            if (found) return found;
        }
    }
    return null;
};

const mergeChildrenAtPath = (nodes: SchemaNode[], treePath: string, children: SchemaNode[], basePath: string = ''): SchemaNode[] => {
    return nodes.map(node => {
        const currentPath = basePath ? `${basePath}.${node.name}` : node.name;
        if (currentPath === treePath) {
            return { ...node, children, expandable: children.length > 0 };
        }
        if (node.children?.length) {
            return { ...node, children: mergeChildrenAtPath(node.children, treePath, children, currentPath) };
        }
        return node;
    });
};

const SchemaDiscoveryModal: React.FC<Props> = ({ onClose, schemaData: initialData, onSelect, onExpand, onBuildMetadata, sourceType }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
    const [treeData, setTreeData] = useState<SchemaNode[]>(initialData);

    const handleChildrenLoaded = useCallback((treePath: string, children: SchemaNode[]) => {
        setTreeData(prev => mergeChildrenAtPath(prev, treePath, children));
    }, []);

    const togglePath = (path: string) => {
        const pathsToToggle = findPathsForNode(treeData, path) ?? [path];
        setSelectedPaths(prev => {
            const isAdding = !prev.includes(path);
            if (isAdding) {
                const toAdd = new Set([...prev, ...pathsToToggle]);
                return Array.from(toAdd);
            } else {
                const toRemove = new Set(pathsToToggle);
                return prev.filter(p => !toRemove.has(p));
            }
        });
    };

    const filterNodes = (nodes: SchemaNode[]): SchemaNode[] => {
        if (!searchQuery) return nodes;
        return nodes.map(node => ({
            ...node,
            children: node.children ? filterNodes(node.children) : undefined
        })).filter(node =>
            node.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (node.children && node.children.length > 0)
        );
    };

    const displayData = filterNodes(treeData);

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <div className="relative w-full max-w-xl bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800 bg-neutral-950/50">
                    <div>
                        <h2 className="text-white font-bold flex items-center gap-2">
                            <Search className="w-4 h-4 text-indigo-400" />
                            Schema Discovery
                        </h2>
                        <p className="text-[10px] text-gray-500 mt-0.5 uppercase tracking-wider font-semibold">Browse and select data objects</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="px-6 py-3 border-b border-neutral-800">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                            type="text"
                            placeholder="Filter tables and columns..."
                            className="w-full bg-neutral-800 border-none rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                {/* Tree Content */}
                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar min-h-[400px]">
                    {displayData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-600">
                            <Database className="w-12 h-12 mb-3 opacity-20" />
                            <p className="text-sm">No schema objects found</p>
                        </div>
                    ) : (
                        displayData.map((node, idx) => (
                            <TreeNode
                                key={node.path || idx}
                                node={node}
                                level={0}
                                onToggle={togglePath}
                                isSelected={selectedPaths.includes(node.name)}
                                path=""
                                selectedPaths={selectedPaths}
                                onExpand={onExpand}
                                onChildrenLoaded={handleChildrenLoaded}
                            />
                        ))
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-neutral-800 bg-neutral-950/30 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        {selectedPaths.length > 0 && (
                            <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-1 rounded-full font-medium">
                                {selectedPaths.length} selected
                            </span>
                        )}
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-white text-sm font-medium px-4 py-2 transition-colors"
                        >
                            Cancel
                        </button>
                                <button
                                    disabled={selectedPaths.length === 0}
                                    onClick={() => {
                                        onSelect(selectedPaths);
                                        if (onBuildMetadata) {
                                            const hint = (sourceType || '').toLowerCase();
                                            const isObjectStorage = hint.includes('s3') || hint.includes('object');
                                            const isFileStorage = hint.includes('onedrive') || hint.includes('one_drive') || hint.includes('sharepoint') || hint.includes('gdrive') || hint.includes('google') || hint.includes('dropbox') || hint.includes('file_storage') || hint.includes('cloud');
                                            const json = isFileStorage
                                                ? buildFileStorageMetadata(treeData, selectedPaths, sourceType)
                                                : isObjectStorage
                                                    ? buildObjectStorageMetadata(treeData, selectedPaths, sourceType)
                                                    : buildRelationalMetadata(treeData, selectedPaths, sourceType);
                                            onBuildMetadata(json);
                                        }
                                    }}
                                    className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 text-white rounded-lg px-6 py-2 text-sm font-bold shadow-lg shadow-indigo-600/20 transition-all flex items-center gap-2"
                                >
                                    <CheckCircle2 className="w-4 h-4" />
                                    Add Selected ({selectedPaths.length})
                                </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchemaDiscoveryModal;
